import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Copy, Eye, EyeOff, Save } from 'lucide-react';
import DashboardLayout from '../DashboardLayout';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const PreguntasManagement = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    tipo_evaluacion: 'lider_empleado',
    activa: null,
    categoria: null
  });
  const [categorias, setCategorias] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preguntaEdit, setPreguntaEdit] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchPreguntas();
    fetchCategorias();
  }, [filters, searchTerm, pagination.page]);

  const fetchPreguntas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dashboardToken');
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters,
        search: searchTerm || undefined
      };

      const response = await axios.get(`${API_URL}/admin/preguntas`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setPreguntas(response.data.items);
      setPagination({
        ...pagination,
        total: response.data.total,
        pages: response.data.pages
      });
    } catch (error) {
      // console.error('Error al cargar preguntas:', error);
      toast.error('Error al cargar preguntas');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const token = localStorage.getItem('dashboardToken');
      const response = await axios.get(`${API_URL}/admin/preguntas/categorias/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { tipo_evaluacion: filters.tipo_evaluacion }
      });

      setCategorias(response.data);
    } catch (error) {
      // console.error('Error al cargar categorías:', error);
    }
  };

  const handleCreate = () => {
    setPreguntaEdit(null);
    setModalOpen(true);
  };

  const handleEdit = (pregunta) => {
    setPreguntaEdit(pregunta);
    setModalOpen(true);
  };

  const handleDelete = async (id, respuestasCount) => {
    const mensaje = respuestasCount > 0
      ? `Esta pregunta tiene ${respuestasCount} respuestas. ¿Está seguro de eliminarla? Se perderá el historial.`
      : '¿Está seguro de eliminar esta pregunta?';

    if (!window.confirm(mensaje)) return;

    try {
      const token = localStorage.getItem('dashboardToken');
      await axios.delete(`${API_URL}/admin/preguntas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Pregunta eliminada exitosamente');
      fetchPreguntas();
    } catch (error) {
      // console.error('Error al eliminar pregunta:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar pregunta');
    }
  };

  const handleToggleActiva = async (id, activa) => {
    try {
      const token = localStorage.getItem('dashboardToken');
      await axios.put(
        `${API_URL}/admin/preguntas/${id}`,
        { activa: !activa },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Pregunta ${!activa ? 'activada' : 'desactivada'} exitosamente`);
      fetchPreguntas();
    } catch (error) {
      // console.error('Error al cambiar estado:', error);
      toast.error('Error al cambiar estado de la pregunta');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const token = localStorage.getItem('dashboardToken');
      await axios.post(
        `${API_URL}/admin/preguntas/${id}/duplicar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Pregunta duplicada exitosamente');
      fetchPreguntas();
    } catch (error) {
      // console.error('Error al duplicar pregunta:', error);
      toast.error('Error al duplicar pregunta');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Preguntas de Evaluación</h1>
          <p className="text-gray-600 mt-1">Administra las preguntas para evaluaciones de líderes y empleados</p>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar pregunta..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Evaluación</label>
              <select
                value={filters.tipo_evaluacion}
                onChange={(e) => setFilters({ ...filters, tipo_evaluacion: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="lider_empleado">Líder → Empleado</option>
                <option value="empleado_lider">Empleado → Líder (360°)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
              <select
                value={filters.categoria || ''}
                onChange={(e) => setFilters({ ...filters, categoria: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                {categorias.map((cat) => (
                  <option key={cat.nombre} value={cat.nombre}>
                    {cat.nombre} ({cat.total_preguntas})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={filters.activa === null ? '' : filters.activa}
                onChange={(e) => setFilters({ ...filters, activa: e.target.value === '' ? null : e.target.value === 'true' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                <option value="true">Solo Activas</option>
                <option value="false">Solo Inactivas</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Agregar Nueva Pregunta
            </button>
          </div>
        </div>

        {/* Tabla de preguntas */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pregunta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoría
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Peso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orden
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Respuestas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      Cargando...
                    </td>
                  </tr>
                ) : preguntas.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      No se encontraron preguntas
                    </td>
                  </tr>
                ) : (
                  preguntas.map((pregunta) => (
                    <tr key={pregunta.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pregunta.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="max-w-md">{pregunta.pregunta}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          {pregunta.categoria}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pregunta.peso}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pregunta.orden}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {pregunta.respuestas_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          pregunta.activa 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {pregunta.activa ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(pregunta)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActiva(pregunta.id, pregunta.activa)}
                          className={`${pregunta.activa ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'} mr-3`}
                          title={pregunta.activa ? 'Desactivar' : 'Activar'}
                        >
                          {pregunta.activa ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDuplicate(pregunta.id)}
                          className="text-purple-600 hover:text-purple-900 mr-3"
                          title="Duplicar"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(pregunta.id, pregunta.respuestas_count)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                  disabled={pagination.page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Siguiente
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{(pagination.page - 1) * pagination.per_page + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.per_page, pagination.total)}</span> de{' '}
                    <span className="font-medium">{pagination.total}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      Siguiente
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Crear/Editar */}
      {modalOpen && (
        <PreguntaModal
          pregunta={preguntaEdit}
          tipoEvaluacion={filters.tipo_evaluacion}
          categorias={categorias}
          onClose={() => setModalOpen(false)}
          onSave={() => {
            setModalOpen(false);
            fetchPreguntas();
            fetchCategorias();
          }}
        />
      )}
    </DashboardLayout>
  );
};

// Modal Component
const PreguntaModal = ({ pregunta, tipoEvaluacion, categorias, onClose, onSave }) => {
  const [formData, setFormData] = useState(pregunta || {
    pregunta: '',
    categoria: '',
    tipo_evaluacion: tipoEvaluacion,
    peso: 1.0,
    orden: 0,
    activa: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('dashboardToken');
      const url = pregunta
        ? `${API_URL}/admin/preguntas/${pregunta.id}`
        : `${API_URL}/admin/preguntas`;

      const method = pregunta ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Pregunta ${pregunta ? 'actualizada' : 'creada'} exitosamente`);
      onSave();
    } catch (error) {
      // console.error('Error al guardar pregunta:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar pregunta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {pregunta ? 'Editar Pregunta' : 'Nueva Pregunta'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pregunta *
              </label>
              <textarea
                value={formData.pregunta}
                onChange={(e) => setFormData({ ...formData, pregunta: e.target.value })}
                maxLength={500}
                rows={3}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Escribe la pregunta de evaluación..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.pregunta.length}/500 caracteres
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoría *
              </label>
              <input
                type="text"
                list="categorias-list"
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Productividad, Liderazgo, Comunicación..."
              />
              <datalist id="categorias-list">
                {categorias.map((cat) => (
                  <option key={cat.nombre} value={cat.nombre} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Evaluación *
              </label>
              <select
                value={formData.tipo_evaluacion}
                onChange={(e) => setFormData({ ...formData, tipo_evaluacion: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="lider_empleado">Líder → Empleado</option>
                <option value="empleado_lider">Empleado → Líder (360°)</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Peso
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={formData.peso}
                  onChange={(e) => setFormData({ ...formData, peso: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Mayor peso = mayor importancia
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Orden
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.orden}
                  onChange={(e) => setFormData({ ...formData, orden: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Orden de aparición en el formulario
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="activa"
                checked={formData.activa}
                onChange={(e) => setFormData({ ...formData, activa: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="activa" className="ml-2 block text-sm text-gray-900">
                Pregunta activa (aparecerá en nuevas evaluaciones)
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreguntasManagement;
